import type { ChangeEvent, FormEvent, JSX } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button, Input, useToast } from '@artosera/ui';
import { usePeut } from '../../../auth/usePeut.js';
import { estElevationAnnulee, useElevation } from '../../../components/useElevation.js';
import { deposerContratOuverture, transitionOuverture } from '../../../lib/api.js';
import { formaterDate, formaterOctets } from '../../../lib/format.js';
import { dateEnSaisie, messageErreurOuverture } from '../../../lib/ouvertures.js';
import formStyles from '../../../styles/form.module.css';
import styles from '../Ouvertures.module.css';
import { Champ, descriptionChamp, type PropsEtape } from './commun.js';

/** Étape 3 : référence, date de signature, PDF signé. Le mandat de reprise vit dans la fiche galerie. */
export function EtapeContrat({
  dossier,
  enCours,
  lectureSeule,
  sauver,
  adopter,
  allerA,
}: PropsEtape): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const { toast } = useToast();
  const elevation = useElevation();
  const champFichier = useRef<HTMLInputElement>(null);

  const [reference, setReference] = useState(dossier?.contrat.reference ?? '');
  const [signeLe, setSigneLe] = useState(dateEnSaisie(dossier?.contrat.signeLe ?? null));
  const [fichier, setFichier] = useState<File | null>(null);
  const [depot, setDepot] = useState(false);
  const [erreurDepot, setErreurDepot] = useState<string | null>(null);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});

  const piece = dossier?.contrat.piece ?? null;
  const peutDeposer = peut('console:manage-mandate') && dossier !== null && !lectureSeule;

  const deposer = async (): Promise<void> => {
    if (!dossier) return;
    if (!fichier) {
      setErreurDepot(t('ouvertures.contrat.fichierAttendu'));
      return;
    }
    setErreurDepot(null);
    setDepot(true);
    try {
      const mis = await elevation.executer(() => deposerContratOuverture(dossier.id, fichier));
      adopter(mis);
      setFichier(null);
      if (champFichier.current) champFichier.current.value = '';
      toast({ title: t('ouvertures.contrat.depose'), closeLabel: t('saisies.suppression.fermer') });
    } catch (erreur) {
      if (!estElevationAnnulee(erreur)) setErreurDepot(messageErreurOuverture(t, erreur));
    } finally {
      setDepot(false);
    }
  };

  const surEnvoi = (evenement: FormEvent): void => {
    evenement.preventDefault();
    const prochaines: Record<string, string> = {};
    if (reference.trim() === '') prochaines.reference = t('ouvertures.contrat.erreurs.reference');
    if (signeLe === '') prochaines.signeLe = t('ouvertures.contrat.erreurs.signeLe');
    setErreurs(prochaines);
    if (Object.keys(prochaines).length > 0) return;
    void sauver(
      { contrat: { reference: reference.trim(), signeLe } },
      4,
      // Le dossier enregistré : s'il peut passer à « Contrat signé », il y passe.
      async (enregistre) => {
        if (!enregistre.transitionsPossibles.includes('contrat_signe')) return enregistre;
        const suivant = await transitionOuverture(enregistre.id, {
          vers: 'contrat_signe',
          revision: enregistre.revision,
        });
        toast({
          title: t('ouvertures.contrat.passeSigne'),
          closeLabel: t('saisies.suppression.fermer'),
        });
        return suivant;
      },
    );
  };

  return (
    <>
      <form className={styles.etape} onSubmit={surEnvoi} noValidate>
        <header>
          <h2>{t('ouvertures.contrat.titre')}</h2>
          <p>{t('ouvertures.contrat.texte')}</p>
        </header>

        <fieldset className={styles.groupe} disabled={lectureSeule}>
          <div className={styles.groupeGrille}>
            <Champ
              id="ouv-reference"
              label={t('ouvertures.contrat.reference')}
              erreur={erreurs.reference}
            >
              <Input
                id="ouv-reference"
                mono
                value={reference}
                onChange={(evenement) => setReference(evenement.target.value)}
                {...descriptionChamp('ouv-reference', { erreur: Boolean(erreurs.reference) })}
              />
            </Champ>
            <Champ
              id="ouv-signe-le"
              label={t('ouvertures.contrat.signeLe')}
              erreur={erreurs.signeLe}
            >
              <Input
                id="ouv-signe-le"
                type="date"
                mono
                value={signeLe}
                onChange={(evenement) => setSigneLe(evenement.target.value)}
                {...descriptionChamp('ouv-signe-le', { erreur: Boolean(erreurs.signeLe) })}
              />
            </Champ>
          </div>

          <hr className={styles.separateur} />

          <h3>{t('ouvertures.contrat.piece')}</h3>
          <div className={styles.piece}>
            {piece ? (
              <>
                <p>
                  {t('ouvertures.contrat.pieceDepose', {
                    date: formaterDate(piece.deposeLe, ''),
                    par: piece.par,
                    taille: formaterOctets(piece.octets),
                  })}
                </p>
                <p className={styles.empreinte}>
                  {t('ouvertures.contrat.empreinte', { sha: piece.sha256.slice(0, 16) })}
                </p>
              </>
            ) : (
              <p>{t('ouvertures.contrat.pieceAucune')}</p>
            )}
          </div>
          {peutDeposer ? (
            <div className={styles.fichier}>
              <input
                ref={champFichier}
                type="file"
                accept="application/pdf"
                aria-label={t('ouvertures.contrat.choisir')}
                onChange={(evenement: ChangeEvent<HTMLInputElement>) =>
                  setFichier(evenement.target.files?.[0] ?? null)
                }
              />
              <Button variant="secondary" onClick={() => void deposer()} loading={depot}>
                {depot ? t('ouvertures.contrat.depot') : t('ouvertures.contrat.deposer')}
              </Button>
            </div>
          ) : (
            <p className={formStyles.aide}>{t('ouvertures.contrat.reserveDirection')}</p>
          )}
          {erreurDepot ? (
            <p className={formStyles.erreur} role="alert">
              {erreurDepot}
            </p>
          ) : null}

          <hr className={styles.separateur} />

          <h3>{t('ouvertures.contrat.mandatTitre')}</h3>
          <p className={styles.info}>
            {t('ouvertures.contrat.mandatTexte')}{' '}
            {dossier?.galleryId ? (
              <Link to={`/galeries/${dossier.galleryId}/reprise`} className="lien-cible">
                {t('ouvertures.contrat.mandatLien')}
              </Link>
            ) : null}
          </p>
        </fieldset>

        <div className={styles.actionsEtape}>
          <Button variant="ghost" onClick={() => allerA(2)}>
            {t('ouvertures.assistant.precedent')}
          </Button>
          <div>
            <Button type="submit" loading={enCours} disabled={lectureSeule}>
              {enCours
                ? t('ouvertures.assistant.enregistrement')
                : t('ouvertures.assistant.continuer')}
            </Button>
          </div>
        </div>
      </form>
      {/* Hors du formulaire : un événement de la boîte ne remonte pas à son envoi. */}
      {elevation.dialogue}
    </>
  );
}
